import React, { useEffect, useRef, useState } from 'react';
import { content, colors } from '../data/content';
import { contact, getWhatsAppLink } from '../data/contact';
import { MessageCircle, ArrowRight, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

const Hero = ({ language }) => {
  const currentContent = content[language].hero;
  const isRTL = language === 'ar';
  const heroRef = useRef(null);
  const [currentImage, setCurrentImage] = useState(0);
  
  const productImages = [
    '/tunagwaizi-uae1.png',
    '/tunagwaizi-uae3.png',
    '/tunagwaizi-uae2.png'
  ];

  // Auto-slide images every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % productImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [productImages.length]);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  return (
    <section
      id="hero"
      ref={heroRef}
      style={{
        position: 'relative',
        minHeight: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: `linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #ffffff 100%)`,
        paddingTop: '80px',
        paddingBottom: 'clamp(20px, 4vw, 40px)'
      }}
    >
      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0
      }}>
        {/* Gradient Orbs */}
        <div style={{
          position: 'absolute',
          top: '10%',
          [isRTL ? 'right' : 'left']: '10%',
          width: '400px',
          height: '400px',
          background: `radial-gradient(circle, ${colors.primary}15 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(60px)',
          animation: 'float 6s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '10%',
          [isRTL ? 'left' : 'right']: '10%',
          width: '500px',
          height: '500px',
          background: `radial-gradient(circle, ${colors.secondary}15 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(80px)',
          animation: 'float 8s ease-in-out infinite reverse'
        }}></div>
        
        {/* Floating Particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              background: `rgba(${Math.random() > 0.5 ? '191, 10, 29' : '38, 60, 125'}, ${Math.random() * 0.3 + 0.2})`,
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 5 + 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: 'clamp(20px, 4vw, 40px) clamp(16px, 3vw, 32px)',
        position: 'relative',
        zIndex: 10,
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 'clamp(20px, 4vw, 40px)',
        alignItems: 'center'
      }}>
        {/* Text Content */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(12px, 3vw, 24px)',
          order: isRTL ? 2 : 1,
          animation: 'fadeInUp 1s ease-out'
        }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}10 100%)`,
            borderRadius: '50px',
            width: 'fit-content',
            border: `1px solid ${colors.accent}40`,
            animation: 'fadeInUp 0.8s ease-out 0.2s both'
          }}>
            <Sparkles size={16} style={{ color: colors.accent }} />
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: colors.secondary,
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
            }}>
              {isRTL ? 'جودة فاخرة منذ 1979' : 'Premium Quality Since 1979'}
            </span>
          </div>

          <h1 style={{
            fontSize: 'clamp(1.75rem, 5vw, 3rem)',
            fontWeight: 800,
            lineHeight: isRTL ? 1.65 : 1.2,
            color: colors.secondary,
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
            margin: 0,
            background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'fadeInUp 0.8s ease-out 0.4s both',
            letterSpacing: language === 'ar' ? '0' : '-0.5px'
          }}>
            {currentContent.headline}
          </h1>

          {currentContent.subheadline && (
            <h2 style={{
              fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
              fontWeight: 600,
              lineHeight: isRTL ? 1.6 : 1.4,
              color: colors.primary,
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
              margin: '-8px 0 0 0',
              animation: 'fadeInUp 0.8s ease-out 0.5s both'
            }}>
              {currentContent.subheadline}
            </h2>
          )}

          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.125rem)',
            color: colors.text,
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
            lineHeight: isRTL ? 1.7 : 1.6,
            margin: 0,
            animation: 'fadeInUp 0.8s ease-out 0.6s both',
            maxWidth: '650px'
          }}>
            {currentContent.description}
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'clamp(8px, 2vw, 12px)',
            justifyContent: 'flex-start',
            animation: 'fadeInUp 0.8s ease-out 0.8s both'
          }}>
            <button
              onClick={(e) => {
                e.preventDefault();
                const element = document.getElementById('wholesale');
                if (element) {
                  const headerHeight = 90;
                  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                  const offsetPosition = elementPosition - headerHeight;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }}
              style={{
                padding: 'clamp(12px, 2.5vw, 16px) clamp(20px, 4vw, 32px)',
                borderRadius: '16px',
                color: 'white',
                fontWeight: 600,
                fontSize: 'clamp(15px, 3vw, 18px)',
                textDecoration: 'none',
                boxShadow: `0 8px 24px ${colors.primary}40`,
                background: `linear-gradient(135deg, ${colors.primary} 0%, #9a0818 100%)`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                position: 'relative',
                overflow: 'hidden',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                e.currentTarget.style.boxShadow = `0 12px 32px ${colors.primary}50`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = `0 8px 24px ${colors.primary}40`;
              }}
            >
              <span>{currentContent.orderButton}</span>
              <ArrowRight size={20} style={{ 
                transform: isRTL ? 'scaleX(-1)' : 'none',
                transition: 'transform 0.3s ease'
              }} />
            </button>

            <a
              href={getWhatsAppLink(contact.phone.whatsapp)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: 'clamp(12px, 2.5vw, 16px) clamp(20px, 4vw, 32px)',
                borderRadius: '16px',
                background: 'white',
                fontWeight: 600,
                fontSize: 'clamp(15px, 3vw, 18px)',
                textDecoration: 'none',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                color: '#25D366',
                border: '2px solid #25D366',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                e.currentTarget.style.background = '#25D366';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(37, 211, 102, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#25D366';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.1)';
              }}
            >
              <MessageCircle size={20} />
              <span>{currentContent.whatsappButton}</span>
            </a>
          </div>

        </div>

        {/* Image Content - Carousel */}
        <div style={{
          position: 'relative',
          order: isRTL ? 1 : 2,
          animation: 'fadeInUp 1s ease-out 0.6s both'
        }}>
          <div style={{
            position: 'relative',
            borderRadius: '32px',
            overflow: 'hidden',
            boxShadow: '0 32px 64px rgba(0, 0, 0, 0.2)',
            transition: 'transform 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
          >
            <div style={{
              width: '100%',
              height: 'clamp(280px, 50vw, 600px)',
              background: `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.secondary}10 100%)`,
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Image Carousel */}
              {productImages.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Tuna Gwaizi Product ${index + 1}`}
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    opacity: currentImage === index ? 1 : 0,
                    transform: currentImage === index ? 'scale(1)' : 'scale(1.05)',
                    transition: 'opacity 0.6s ease-in-out, transform 0.6s ease-in-out'
                  }}
                />
              ))}
              
              {/* Light Overlay */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, rgba(0,0,0,0.15) 100%)',
                pointerEvents: 'none',
                zIndex: 1
              }}></div>
              
              {/* Navigation Arrows */}
              {/* Previous Button - Right side in RTL, Left side in LTR */}
              <button
                onClick={prevImage}
                style={{
                  position: 'absolute',
                  top: '50%',
                  [isRTL ? 'right' : 'left']: 'clamp(8px, 2vw, 16px)',
                  transform: 'translateY(-50%)',
                  width: 'clamp(36px, 8vw, 44px)',
                  height: 'clamp(36px, 8vw, 44px)',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease',
                  zIndex: 10
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.primary;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.color = colors.secondary;
                }}
              >
                {isRTL ? (
                  <ChevronRight size={20} style={{ color: 'inherit' }} />
                ) : (
                  <ChevronLeft size={20} style={{ color: 'inherit' }} />
                )}
              </button>
              
              {/* Next Button - Left side in RTL, Right side in LTR */}
              <button
                onClick={nextImage}
                style={{
                  position: 'absolute',
                  top: '50%',
                  [isRTL ? 'left' : 'right']: 'clamp(8px, 2vw, 16px)',
                  transform: 'translateY(-50%)',
                  width: 'clamp(36px, 8vw, 44px)',
                  height: 'clamp(36px, 8vw, 44px)',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease',
                  zIndex: 10
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.primary;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.color = colors.secondary;
                }}
              >
                {isRTL ? (
                  <ChevronLeft size={20} style={{ color: 'inherit' }} />
                ) : (
                  <ChevronRight size={20} style={{ color: 'inherit' }} />
                )}
              </button>

              {/* Dots Indicator */}
              <div style={{
                position: 'absolute',
                bottom: 'clamp(12px, 3vw, 20px)',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 'clamp(6px, 2vw, 10px)',
                zIndex: 10
              }}>
                {productImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    style={{
                      width: currentImage === index ? 'clamp(18px, 4vw, 24px)' : 'clamp(8px, 2vw, 10px)',
                      height: 'clamp(8px, 2vw, 10px)',
                      borderRadius: '5px',
                      background: currentImage === index ? colors.primary : 'rgba(255, 255, 255, 0.7)',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                ))}
              </div>
            </div>
            
            {/* Floating Badge */}
            <div style={{
              position: 'absolute',
              bottom: 'clamp(45px, 10vw, 80px)',
              [isRTL ? 'right' : 'left']: 'clamp(12px, 3vw, 32px)',
              padding: 'clamp(8px, 2vw, 16px) clamp(14px, 3vw, 28px)',
              borderRadius: 'clamp(10px, 2vw, 16px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
              fontWeight: 700,
              fontSize: 'clamp(14px, 3.5vw, 24px)',
              background: `linear-gradient(135deg, ${colors.accent} 0%, #fdd835 100%)`,
              color: colors.secondary,
              animation: 'pulse 2s ease-in-out infinite',
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
              zIndex: 5
            }}>
              {isRTL ? '100% طبيعي' : '100% Natural'}
            </div>
          </div>

          {/* Decorative Elements */}
          <div style={{
            position: 'absolute',
            top: '-20px',
            [isRTL ? 'left' : 'right']: '-20px',
            width: '120px',
            height: '120px',
            background: `linear-gradient(135deg, ${colors.accent}30 0%, ${colors.primary}30 100%)`,
            borderRadius: '50%',
            filter: 'blur(40px)',
            zIndex: -1,
            animation: 'float 4s ease-in-out infinite'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '-20px',
            [isRTL ? 'right' : 'left']: '-20px',
            width: '100px',
            height: '100px',
            background: `linear-gradient(135deg, ${colors.secondary}30 0%, ${colors.primary}30 100%)`,
            borderRadius: '50%',
            filter: 'blur(40px)',
            zIndex: -1,
            animation: 'float 5s ease-in-out infinite reverse'
          }}></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;